import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { Message } from '../types';

let channelSeq = 0;

/**
 * Subscribes to live message updates for one conversation row and calls
 * `onMessages` with the new message list on every UPDATE.
 *
 * Each mount gets its own channel topic. `supabase.channel(topic)` returns an
 * already-registered channel when one with the same topic still exists, so a
 * fixed topic can hand back an already-subscribed channel on a fast remount and
 * the `postgres_changes` binding then throws — which kills live delivery
 * silently until reload. A per-mount suffix keeps every mount on a fresh
 * channel.
 */
export function useConversationMessages(
  conversationId: string | null | undefined,
  onMessages: (messages: Message[]) => void,
) {
  const onMessagesRef = useRef(onMessages);
  onMessagesRef.current = onMessages;

  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase
      .channel(`conv:${conversationId}:${++channelSeq}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'conversations',
        filter: `id=eq.${conversationId}`,
      }, (payload) => {
        const updated = payload.new as { messages: Message[] };
        onMessagesRef.current(updated.messages);
      })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [conversationId]);
}
